import {
  DateInput,
  EcSwitchInput,
  SelectInput,
  TextInput,
} from '@components/inputs'
import { ControlledAutocompleteInput } from '@components/inputs/ControlledAutocompleteInput'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, Grid, TextField, Typography } from '@mui/material'
import {
  ConsoleConstructorEnum,
  CreateMedicalEquipmentDto,
  MagnetPowerEnum,
  MedicalEquipment,
  RoomType,
  UpdateMedicalEquipmentDto,
} from '@services/api'
import { useSelector } from '@state/store'
import { findAllRooms } from '@state/thunks/roomThunk'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { equipmentBrand } from '@utils/constants'
import { medicalEquipmentSchema } from '@utils/schemas'
import moment from 'moment'
import { MouseEventHandler, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import {
  createMedicalEquipment,
  updateMedicalEquipment,
} from '../../../state/thunks/medicalEquipmentThunk'
import { LinacForm, MammographeForm } from './EquipmentsForms'
import {
  getModality,
  getCategory,
  getZoneGeographique,
  getFiness,
} from '@state/thunks/ForfaitThunk'
import { useIntl } from 'react-intl'

type AddEquipFormProps = {
  row?: MedicalEquipment
  onClose?: () => void
  onCancelButtonPress?: (
    ev: MouseEventHandler<HTMLButtonElement> | undefined,
  ) => void
}

export function MedicalEquipmentForm({ row, onClose }: AddEquipFormProps) {
  const dispatch = useDispatch()
  const intl = useIntl()
  // State
  const [selectedType, setSelectedType] = useState<number | null>(
    () => row?.ftConfig?.modalite_id ?? null,
  )
  const [selectedSite, setSelectedSite] = useState<number | null>(
    () => row?.siteId ?? null,
  )
  const [selectedCategory, setSelectedCategory] = useState<number | null>(
    () => row?.ftConfig?.categorie_id ?? null,
  )
  const [selectedZone, setSelectedZone] = useState<number | null>(
    () => row?.ftConfig?.ft_zone_geographique ?? null,
  )

  const [selectedFiness, setSelectedFiness] = useState<number | null>(
    () => row?.ftConfig?.fr_finess_id ?? null,
  )
  const [ftNumeroAutorisation, setFtNumeroAutorisation] = useState<string>(
    () => row?.ftConfig?.ft_numero_autorisation || '',
  )
  const [ftNumero, setFtNumero] = useState<number | null>(
    () => row?.ftConfig?.ft_numero ?? null,
  )

  // Submit
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<CreateMedicalEquipmentDto>({
    resolver: yupResolver(medicalEquipmentSchema),
    defaultValues: {
      code: row?.code || '',
      commissioningDate: row?.commissioningDate || moment().toDate(),
      dose: row?.dose || false,
      manufacturer: row?.manufacturer || '',
      model: row?.model || '',
      serialNumber: row?.serialNumber || '',
      shared: row?.shared || false,
      examRoomId: row?.examRoom?.id,
      consoleConstructor: row?.consoleConstructor || undefined,
      magnetPower: row?.magnetPower || undefined,
      sharder: row?.sharder || undefined,
    },
  })

  const onSubmit = (values: CreateMedicalEquipmentDto) => {
    if (!selectedSite || !selectedType) {
      console.log('nnnnn')
      return
    }

    const isConsoleConstructor = ['CT', 'PT', 'NM', 'MR', 'MG'].includes(
      selectedModalityCode,
    )
    const isDigital = ['RCT', 'MG'].includes(selectedModalityCode)
    const isMagnetPower = ['MR'].includes(selectedModalityCode)

    const dto: CreateMedicalEquipmentDto = {
      code: values.code,
      commissioningDate: values.commissioningDate,
      dose: values.dose,
      manufacturer: values.manufacturer,
      model: values.model,
      serialNumber: values.serialNumber,
      shared: values.shared,
      sharder: values.sharder,
      consoleConstructor: values.consoleConstructor,
      magnetPower: values.magnetPower,
      examRoomId: values.examRoomId,
      siteId: selectedSite,
      type: selectedModalityCode,
      modaliteId: selectedType,
      categorieId: selectedCategory,
      ftZoneGeographique: selectedZone,
      finessId: selectedFiness,
      ftNumeroAutorisation: ftNumeroAutorisation || null,
      ftNumero: ftNumero,
      ...(isDigital && { digital: values.digital }),
      ...(isMagnetPower && { magnetPower: values.magnetPower }),
      ...(isConsoleConstructor && {
        consoleConstructor: values.consoleConstructor,
      }),
    }
    if (row?.id) {
      dispatch(
        updateMedicalEquipment({
          id: row.id,
          body: dto as UpdateMedicalEquipmentDto,
        }),
      )
    } else {
      dispatch(createMedicalEquipment(dto))
    }

    if (onClose) {
      onClose()
    }
  }

  // Reducers
  const { sites, rooms, modalities, categories, zones, finess } = useSelector(
    ({ site, room, forfaitsTechnique }) => ({
      sites: site.sites.datas,
      rooms: room.rooms,
      modalities: forfaitsTechnique.modalities,
      categories: forfaitsTechnique.categories,
      zones: forfaitsTechnique.zones,
      finess: forfaitsTechnique.finess,
    }),
  )
  // Map all sites
  const sitesOptions = useMemo(
    () =>
      sites.map((site) => ({
        value: site.id as number,
        label: site.label,
      })),
    [sites],
  )

  const selectedModalityCode = useMemo(
    () => modalities.find((m) => m.id === selectedType)?.code || '',
    [modalities, selectedType],
  )

  useEffect(() => {
    const nextSite = row?.siteId ?? null
    setSelectedSite((prev) => (prev === nextSite ? prev : nextSite))
  }, [row?.siteId])

  useEffect(() => {
    const nextType = row?.ftConfig?.modalite_id ?? null
    setSelectedType((prev) => (prev === nextType ? prev : nextType))
  }, [row?.ftConfig?.modalite_id])

  useEffect(() => {
    dispatch(findAllSites())
    dispatch(findAllRooms())
    dispatch(getModality())
    dispatch(getCategory())
    dispatch(getZoneGeographique())
    dispatch(getFiness())
  }, [])

  const examRoomsOptions = useMemo(
    () =>
      rooms.reduce(
        (acc, _examRoom) => {
          if (
            _examRoom.type === RoomType.ExamRoom &&
            _examRoom.siteId === selectedSite
          ) {
            acc.push({
              label: _examRoom.label,
              value: _examRoom.id,
            })
          }
          return acc
        },
        [] as {
          label: string
          value: number
        }[],
      ),
    [rooms, selectedSite],
  )

  const _handleTypeChange = (ev) => {
    setSelectedType(ev.target.value)
  }

  const _handleSiteChange = (ev) => {
    setSelectedSite(ev.target.value)
  }

  const _handleCategoryChange = (ev) => {
    setSelectedCategory(ev.target.value)
  }

  const _handleZoneChange = (ev) => {
    setSelectedZone(ev.target.value)
  }
  const _handleFinessChange = (ev) => {
    setSelectedFiness(ev.target.value)
  }

  const consoleConstructorOptions = Object.values(ConsoleConstructorEnum).map(
    (_v) => ({
      label: _v,
      value: _v,
    }),
  )
  const magnetPowerOptions = Object.values(MagnetPowerEnum).map((value) => ({
    label: value,
    value,
  }))

  const typeOptions = useMemo(
    () =>
      modalities.map((modality) => ({
        label: modality.libelle,
        value: modality.id,
      })),
    [modalities],
  )

  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        label: category.libelle,
        value: category.id,
      })),
    [categories],
  )

  const zoneOptions = useMemo(
    () =>
      zones.map((zone) => ({
        label: zone.libelle,
        value: zone.code,
      })),
    [zones],
  )

  const finessOptions = useMemo(
    () =>
      finess.map((f) => ({
        label: f.finess || `Finess #${f.id}`,
        value: f.id,
      })),
    [finess],
  )

  return (
    <Grid
      container
      spacing={1}
      justifyContent="center"
      alignItems="center"
      sx={{ pt: '10px', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        component="fieldset"
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          m: 0,
          width: '100%',
        }}
      >
        <legend>
          <Typography variant="body2" sx={{ px: 1 }}>
            Administration
          </Typography>
        </legend>
        <Grid container spacing={1}>
          <Grid item xs={12} xl={6}>
            <TextField
              label={intl.formatMessage({ id: 'ftNumeroAutorisation' })}
              value={ftNumeroAutorisation}
              onChange={(ev) => setFtNumeroAutorisation(ev.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} xl={6}>
            <TextField
              label={intl.formatMessage({ id: 'ftNumero' })}
              type="number"
              value={ftNumero ?? ''}
              onChange={(ev) =>
                setFtNumero(ev.target.value ? Number(ev.target.value) : null)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} xl={6}>
            <SelectInput
              name="zone"
              options={zoneOptions}
              onChange={_handleZoneChange}
              value={selectedZone}
            />
          </Grid>
          <Grid item xs={12} xl={6}>
            <SelectInput
              name="finess"
              options={finessOptions}
              onChange={_handleFinessChange}
              value={selectedFiness}
            />
          </Grid>
          <Grid item xs={12} xl={6}>
            <SelectInput
              name="type"
              options={typeOptions}
              onChange={_handleTypeChange}
              value={selectedType}
            />
          </Grid>
        </Grid>
      </Box>

      <Box
        component="fieldset"
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          m: 0,
          width: '100%',
        }}
      >
        <legend>
          <Typography variant="body2" sx={{ px: 1 }}>
            Technique
          </Typography>
        </legend>
        <Grid container spacing={1}>
          <Grid item xs={12} xl={6}>
            <SelectInput
              name="site"
              value={selectedSite}
              options={sitesOptions}
              onChange={_handleSiteChange}
            />
          </Grid>
          <Grid item xs={12} xl={6}>
            <SelectInput
              options={examRoomsOptions}
              control={control}
              name="examRoomId"
              errors={errors}
            />
          </Grid>
           <Grid item xs={12} xl={6}>
            <TextInput name="code" control={control} errors={errors} />
          </Grid>
          <Grid item xs={12} xl={6}>
            <SelectInput
              name="category"
              options={categoryOptions}
              onChange={_handleCategoryChange}
              value={selectedCategory}
            />
          </Grid>
         
          {selectedType && (
            <>
              <Grid item xs={12} xl={6}>
                <ControlledAutocompleteInput
                  input="manufacturer"
                  control={control}
                  errors={errors}
                  options={equipmentBrand}
                  defaultValue={
                    row?.manufacturer
                      ? equipmentBrand.find(
                          (brand) => brand.value === row.manufacturer,
                        ) || null
                      : null
                  }
                  label={intl.formatMessage({ id: 'manufacturer' })}
                />
              </Grid>
              <Grid item xs={12} xl={6}>
                <TextInput name="model" control={control} errors={errors} />
              </Grid>
              <Grid item xs={12} xl={6}>
                <TextInput
                  name="serialNumber"
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={12} xl={6}>
                <DateInput
                  control={control}
                  errors={errors}
                  name="commissioningDate"
                />
              </Grid>
              {/* <Grid item xs={6}>
            <EcSwitchInput control={control} input="dose" />
          </Grid> */}
              <Grid item xs={12} xl={6}>
                <SelectInput
                  options={magnetPowerOptions}
                  control={control}
                  name="magnetPower"
                />
              </Grid>

              {['CT', 'PT', 'NM', 'MR', 'MG'].includes(
                selectedModalityCode,
              ) && (
                <>
                  <Grid item xs={12} xl={6}>
                    <SelectInput
                      options={consoleConstructorOptions}
                      control={control}
                      name="consoleConstructor"
                      errors={errors}
                    />
                  </Grid>

                  <Grid item xs={12} xl={6}></Grid>
                </>
              )}
              {['RCT', 'MG'].includes(selectedModalityCode) && (
                <Grid item xs={12}>
                  <EcSwitchInput
                    control={control}
                    input="digital"
                    defaultValue={0}
                  />
                </Grid>
              )}

              <Grid item xs={12} xl={6}></Grid>
              {['LINAC'].includes(selectedModalityCode) && (
                <LinacForm control={control} />
              )}
              {['MG'].includes(selectedModalityCode) && (
                <Grid item xs={12}>
                  <MammographeForm control={control} />
                </Grid>
              )}

              <Grid item xs={12}>
                <EcSwitchInput control={control} input="shared" name="shared" />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      <Grid container gap={2} justifyContent="flex-end" mt={3}>
        {onClose && (
          <Button onClick={onClose} color="primary" variant="outlined">
            Annuler
          </Button>
        )}
        <Button
          onClick={handleSubmit(onSubmit)}
          color="secondary"
          variant="contained"
        >
          {row ? 'Enregistrer' : 'Ajouter'}
        </Button>
      </Grid>
    </Grid>
  )
}
